import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { Stasher, UserRole } from '@stash/shared-types';
import type { RootState } from '../reducers';
import DataService from '../services/data-service';

export const useRoleCheck = () => {
  const [stasher, setStasher] = useState<Stasher | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  useEffect(() => {
    const loadStasher = async (): Promise<void> => {
      if (!currentUser?.userId) {
        setStasher(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const dataService = new DataService();
        const stasherData = (await dataService.getAuthenticatedStasher()) as Stasher;
        setStasher(stasherData);
      } catch (err) {
        console.error('Failed to load Stasher data:', err);
        setError(err as Error);
        setStasher(null);
      } finally {
        setIsLoading(false);
      }
    };

    void loadStasher();
  }, [currentUser?.userId]);

  const hasRole = (role: UserRole | string): boolean => {
    if (!stasher?.userRoles || !Array.isArray(stasher.userRoles)) {
      return false;
    }
    return stasher.userRoles.includes(role as UserRole);
  };

  const isAdmin = (): boolean => hasRole('admin') || hasRole('superAdmin');
  const isSuperAdmin = (): boolean => hasRole('superAdmin');

  return { stasher, isLoading, error, hasRole, isAdmin, isSuperAdmin };
};
